// Sign-up / sign-in modal. Two tabs sharing one form; the only
// difference is which API endpoint we call and a slightly different
// error message vocabulary. Closes on success.

import { useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ApiError } from "../auth/client";
import { licenseStore } from "../auth/license";

export type AuthMode = "signup" | "signin";

export interface AuthModalProps {
  open: boolean;
  initialMode?: AuthMode;
  onClose: () => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials_format:
    "Email must be valid and password must be 8–72 characters.",
  invalid_credentials: "Email or password is incorrect.",
  signup_failed: "Could not sign up. The email may already be registered.",
  license_invalid_or_used:
    "Your activation code has already been used or is no longer valid. Each code creates exactly one account.",
  no_token: "You're not signed in.",
  unauthorized: "Your session expired. Please sign in again.",
};

function readableError(err: unknown): string {
  if (err instanceof ApiError) {
    return ERROR_MESSAGES[err.code] ?? `Error: ${err.code}`;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

export function AuthModal({ open, initialMode = "signin", onClose }: AuthModalProps) {
  const { signin, signup } = useAuth();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (mode === "signup") {
        const code = licenseStore.get();
        if (!code) {
          // Should never happen — the license gate runs before
          // this modal can ever render — but a defensive error
          // beats a confusing 400 from the API.
          setError(
            "Missing activation code. Please reload the page and re-enter your code.",
          );
          return;
        }
        await signup(email, password, code);
      } else {
        await signin(email, password);
      }
      onClose();
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(readableError(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="auth-modal-title">{mode === "signup" ? "Sign up" : "Sign in"}</h2>
        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={"auth-tab" + (mode === "signin" ? " active" : "")}
            aria-selected={mode === "signin"}
            onClick={() => {
              setMode("signin");
              setError(null);
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            className={"auth-tab" + (mode === "signup" ? " active" : "")}
            aria-selected={mode === "signup"}
            onClick={() => {
              setMode("signup");
              setError(null);
            }}
          >
            Sign up
          </button>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={pending}
            />
          </label>
          <label className="auth-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              required
              minLength={8}
              maxLength={72}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={pending}
            />
          </label>
          {error && <div className="auth-error">{error}</div>}
          <div className="auth-actions">
            <button
              type="button"
              className="auth-cancel"
              onClick={onClose}
              disabled={pending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="auth-submit"
              disabled={pending || email.length === 0 || password.length < 8}
            >
              {pending ? "..." : mode === "signup" ? "Create account" : "Sign in"}
            </button>
          </div>
        </form>
        <p className="auth-help">
          Authentication is optional — you can use the sandbox without
          signing in. Signing up lets you save experiments and formulas
          to a personal library that survives reloads and devices.
        </p>
      </div>
    </div>
  );
}
